import { v4 as uuidv4 } from "uuid";
import debounce from "lodash/debounce";

let socket, appInitData, favIconUrl;
let windowMessageEvtListener = false;
let jwtToken = null; // Store JWT token for trackevent
let dashboardMessageHandler = null; // Store dashboard closeAIcare message handler
// Timestamp of the last dashboard open. Used to ignore outside-clicks that
// arrive in the same gesture (or as a delayed "ghost" click) and would
// otherwise tear the overlay down before the iframe request completes —
// visible in DevTools as a canceled dashboard navigation.
let dashboardOpenedAt = 0;
const DASHBOARD_OUTSIDE_CLOSE_GUARD_MS = 300;
let hiInsideAppObserverStarted = false;
let stimulusTrustedWindow = null;
let stimulusTrustedOrigin = null;
let stimulusAnchorTarget = null;
let stimulusPositionCleanup = null;
const mobileTriggerWidth = 600;

const NOTIFICATION_STORAGE_KEY = "notificationState";
const NOTIFICATION_CHECK_MS = 300000; // 5 minutes
let notificationInterval = null;
const FIRST_ACTIVITY_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
let firstActivityInterval = null;
const FIRST_ACTIVITY_RECEIVED_STORAGE_KEY_PREFIX = "HI::FirstActivity::";

const selectorNameElementMap = {};

const HI_WEB_ROOT_ID = "hi-web-root";

const ensureHiMount = () => {
  let host = document.getElementById(HI_WEB_ROOT_ID);
  if (!host) {
    host = document.createElement("div");
    host.id = HI_WEB_ROOT_ID;
    host.setAttribute("data-hi-web", "mount");
    host.style.cssText =
      "position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2147483647;transform:none;filter:none;isolation:isolate;margin:0;padding:0;border:0;background:transparent;";
    document.body.appendChild(host);
    const shadow = host.attachShadow({ mode: "open" });
    const base = document.createElement("style");
    base.textContent = `
      :host {
        display: block;
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
      }
      #icon-container,
      #hi-stimulus,
      #hi-container {
        pointer-events: auto;
      }
      #hi-stimulus {
        position: fixed;
        bottom: 0;
        right: 0;
        z-index: 2147483647;
      }
      #hi-stimulus.hi-stimulus--anchored {
        bottom: auto;
        right: auto;
        top: 0;
        left: 0;
      }
    `;
    shadow.appendChild(base);
    return shadow;
  }
  return host.shadowRoot;
};

const getHiShadowRoot = () =>
  document.getElementById(HI_WEB_ROOT_ID)?.shadowRoot ?? null;

const hiQuery = (selector) =>
  getHiShadowRoot()?.querySelector(selector) ?? null;

const hiGetElementById = (id) => getHiShadowRoot()?.getElementById(id) ?? null;

// Safari macOS can stretch <img width:100%> on first paint when the parent
// flex box has no resolved width yet; intrinsic width/height attributes fix it.
const syncCustomIconIntrinsicSize = (img) => {
  if (!img) {
    return;
  }
  const apply = () => {
    const { naturalWidth, naturalHeight } = img;
    if (!naturalWidth || !naturalHeight) {
      return;
    }
    img.setAttribute("width", String(naturalWidth));
    img.setAttribute("height", String(naturalHeight));
  };
  if (img.complete) {
    apply();
  } else {
    img.addEventListener("load", apply, { once: true });
  }
};

const clearStimulusPositionListeners = () => {
  if (stimulusPositionCleanup) {
    stimulusPositionCleanup();
    stimulusPositionCleanup = null;
  }
};

const updateStimulusAnchorPosition = (stimulusEl, targetEl) => {
  if (!stimulusEl || !targetEl || !targetEl.isConnected) {
    return;
  }
  const rect = targetEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 8;
  const offsetY = 20;
  let top = rect.bottom + offsetY;
  let left = rect.left;
  const w = stimulusEl.offsetWidth || 0;
  const h = stimulusEl.offsetHeight || 0;
  left = Math.min(Math.max(margin, left), Math.max(margin, vw - w - margin));
  top = Math.min(Math.max(margin, top), Math.max(margin, vh - h - margin));
  stimulusEl.style.top = `${Math.round(top)}px`;
  stimulusEl.style.left = `${Math.round(left)}px`;
};

const logger = {
  ...console,
  info: (...props) => {
    console.info(new Date().toISOString(), ...props);
  },
  error: (...props) => {
    console.info(new Date().toISOString(), ...props);
  },
};

// TODO get this from app-init-r response
const currentDomain = window.location.hostname;
// Domain configuration mapping
// Maps domains to objects containing dashboardurldefault and analyticendpoint
const DOMAIN_CONFIG = {
  "really.realegroup.eu": {
    isDisabled: true,
    dashboardurldefault: "https://www.realeitaly.dagamestudio.com",
    customdashboardcloseevent: "closeAIcare",
    // useridswhitelist: [
    //   "D103898",
    //   "DI00490",
    //   "DI00319",
    //   "D103326",
    //   "D103886",
    //   "DY00070",
    //   "D103431",
    //   "DY00170",
    //   "D103515",
    //   "D104168",
    //   "DY00314",
    //   "C104113", // test user; LUCA
    //   "DC2009999", // test user; SPAIN USUARIO ESP
    //   "DE2001418",
    //   "DE2001941",
    //   "DE2001650",
    //   "DE2002112",
    //   "C105175", // Mattia DA
    // ],
    dashboardurlcustomerIdMapping: {
      reale94442dad: "https://www.realeitaly.dagamestudio.com",
      realespain8361aafa: "https://www.realespain.dagamestudio.com",
    },
    displayAsBar: true,
    displayOnMobile: true,
    openFirstTime: true,
    barImage: {
      reale94442dad:
        "https://realeitaly.dagamestudio.com/reale/hiweb/aicare_bar_collection.png",
      realespain8361aafa:
        "https://www.realespain.dagamestudio.com/reale/hiweb/aicare_bar_collection_es.png",
    },
    barMobileImage:
      "https://realeitaly.dagamestudio.com/reale/hiweb/aicare_bar_collection_mobile.png",
    notificationEnabled: true,
    cacheAppInit: true,
    debounceFirstActivityInterval: 5 * 60 * 1000, // 5 minutes
    trackPageVisit: {
      // Italy
      "https://really.realegroup.eu/tamtamy/content/details-78588.action": {
        eventId: "article-policy-visit",
      },
      "https://really.realegroup.eu/tamtamy/content/details-59489.action": {
        eventId: "article-page-visit1",
      },
      "https://really.realegroup.eu/tamtamy/content/details-75447.action": {
        eventId: "article-page-visit2",
      },
      "https://really.realegroup.eu/tamtamy/content/details-68839.action": {
        eventId: "article-page-visit3",
      },
      // Spain
      "https://really.realegroup.eu/tamtamy/esp/explorar-areas/innovacion/ai-care":
        {
          eventId: "group-user-spain",
        },
      "https://really.realegroup.eu/tamtamy/content/details-79560.action": {
        eventId: "article-policy-visit",
      },
      "https://really.realegroup.eu/tamtamy/permalink/content/principios-de-ia-de-reale-la-e-tica-en-el-centro-de-nuestra-estrategia-de-ia":
        {
          eventId: "article-page-visit1",
        },
      "https://really.realegroup.eu/tamtamy/permalink/content/nuevo-glosario-sobre-inteligencia-artificial":
        {
          eventId: "article-page-visit2",
        },
      "https://really.realegroup.eu/tamtamy/permalink/content/inteligencia-artificial-responsable-alfabetizacio-n-digital-en-ia":
        {
          eventId: "article-page-visit3",
        },
      // "https://really.realegroup.eu/tamtamy/content/details-78590.action":{
      //   eventId: "microlearning-submission",
      // },
    },
  },
  "fabbricafuturox-test.tamtamyserver.com": {
    isDisabled: true,
    dashboardurldefault: "https://www.realeitalytest.dagamestudio.com",
    customdashboardcloseevent: "closeAIcare",
    dashboardurlcustomerIdMapping: {
      reale94442dad: "https://www.realeitalytest.dagamestudio.com",
      realespain8361aafa: "https://www.realespaintest.dagamestudio.com",
    },
    displayAsBar: true,
    displayOnMobile: true,
    openFirstTime: true,
    barImage: {
      reale94442dad:
        "https://realeitalytest.dagamestudio.com/reale/hiweb/aicare_bar_collection.png",
      realespain8361aafa:
        "https://realespaintest.dagamestudio.com/reale/hiweb/aicare_bar_collection_es.png",
    },
    barMobileImage:
      "https://realeitalytest.dagamestudio.com/reale/hiweb/aicare_bar_collection_mobile.png",
    notificationEnabled: true,
    cacheAppInit: true,
    debounceFirstActivityInterval: 5 * 60 * 1000, // 5 minutes
    trackPageVisit: {
      // Italy
      "https://fabbricafuturox-test.tamtamyserver.com/tamtamy/news/comunicati":
        {
          eventId: "article-policy-visit",
        },
      "https://fabbricafuturox-test.tamtamyserver.com/tamtamy/our-group/society":
        {
          eventId: "article-page-visit1",
        },
      "https://fabbricafuturox-test.tamtamyserver.com/tamtamy/con-ita/employability/academy/catalog":
        {
          eventId: "article-page-visit2",
        },
      "https://fabbricafuturox-test.tamtamyserver.com/tamtamy/con-ita/news/circolare":
        {
          eventId: "article-page-visit3",
        },
      // Spain
      "https://fabbricafuturox-test.tamtamyserver.com/tamtamy/permalink/content/ti-tulo-de-una-noticia":
        {
          eventId: "group-user-spain",
        },
      "https://fabbricafuturox-test.tamtamyserver.com/tamtamy/esp/news/newsletter":
        {
          eventId: "article-policy-visit",
        },
      "https://fabbricafuturox-test.tamtamyserver.com/tamtamy/esp/news/newsletter-esp":
        {
          eventId: "article-page-visit1",
        },
      "https://fabbricafuturox-test.tamtamyserver.com/tamtamy/esp/our-group/international":
        {
          eventId: "article-page-visit2",
        },
      "https://fabbricafuturox-test.tamtamyserver.com/tamtamy/esp/our-group/sostenibilidad":
        {
          eventId: "article-page-visit3",
        },
    },
  },
  localhost: {
    // for testing purposes
    dashboardurldefault: "https://www.realeitalytest.dagamestudio.com",
    //dashboardurldefault: "http://localhost:3000",
    customdashboardcloseevent: "closeAIcare",
    //useridswhitelist: ["1234567"],
    displayAsBar: true,
    displayOnMobile: true,
    openFirstTime: true,
    barImage: {
      reale94442dad:
        "https://realeitalytest.dagamestudio.com/reale/hiweb/aicare_bar_collection.png",
      realespain8361aafa:
        "https://realespaintest.dagamestudio.com/reale/hiweb/aicare_bar_collection_es.png",
    },
    barMobileImage:
      "https://realeitalytest.dagamestudio.com/reale/hiweb/aicare_bar_collection_mobile.png",
    notificationEnabled: true,
    cacheAppInit: true,
    debounceFirstActivityInterval: 5 * 60 * 1000, // 5 minutes
    trackPageVisit: {
      "http://localhost:3000?testpagevisitarticle1=true": {
        eventId: "article1-page-visit",
      },
      "http://localhost:3000?testpagevisitarticle2=true": {
        eventId: "article2-page-visit",
      },
    },
  },
};

const SIMULATETRACKEVENT = false;
const APP_INIT_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
const APP_INIT_RESPONSE_CACHE_KEY_PREFIX = "HI::appInitResponse::";

const getAppInitResponseCacheKey = (did) =>
  `${APP_INIT_RESPONSE_CACHE_KEY_PREFIX}${did}`;

const shouldSendAppInit = (did) => {
  if (!did || !DOMAIN_CONFIG[currentDomain]?.cacheAppInit) {
    return true;
  }

  const cached = getCachedAppInitResponse(did);
  if (!cached || cached.t == null) {
    return true;
  }

  const lastReceivedAt = Number(cached.t);
  if (!Number.isFinite(lastReceivedAt)) {
    return true;
  }

  return Date.now() - lastReceivedAt >= APP_INIT_CACHE_TTL_MS;
};

const cacheAppInitResponse = (did, data) => {
  if (!did || !data) {
    return;
  }
  try {
    const cachedData = data;
    // {
    //   t: data.t,
    //   channel: data.channel,
    //   dashboardAccess: data.dashboardAccess,
    //   cid: data.cid,
    //   hiWebBtnIcon: data.hiWebBtnIcon,
    //   userstateStartedOn: data.userstateStartedOn,
    // };
    localStorage.setItem(
      getAppInitResponseCacheKey(did),
      JSON.stringify(cachedData)
    );
  } catch (e) {
    logger.error("Failed to cache app-init response", e);
  }
};

const getCachedAppInitResponse = (did) => {
  if (!did) {
    return null;
  }
  const raw = localStorage.getItem(getAppInitResponseCacheKey(did));
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (e) {
    logger.error("Failed to parse cached app-init response", e);
    return null;
  }
};

// Helper function to get dashboard URL considering customerId mapping
const getDashboardUrlFromDomainConfig = () => {
  const domainConfig = DOMAIN_CONFIG[currentDomain];
  if (!domainConfig?.dashboardurldefault) {
    return null;
  }

  const customerId = window.HiConfig?.customerId;
  // Give precedence to customerId mapping if available
  if (domainConfig.dashboardurlcustomerIdMapping && customerId) {
    const mappedUrl = domainConfig.dashboardurlcustomerIdMapping[customerId];
    if (mappedUrl) {
      return mappedUrl;
    }
  }

  // Fallback to default dashboard URL
  return domainConfig.dashboardurldefault;
};

// Normalize URL for trackPageVisit matching: base (origin + path) and search params.
// Treats http://localhost:3000 and http://localhost:3000/ as equivalent.
// Normalizes trailing slashes in paths: /article/123 and /article/123/ are equivalent.
const normalizeUrlForTrackPageVisit = (urlString) => {
  try {
    const parsed = new URL(urlString);
    const path =
      parsed.pathname === "/" || parsed.pathname === ""
        ? ""
        : parsed.pathname.replace(/\/$/, "");
    const base = parsed.origin + path;
    const search = parsed.search;
    return { base, search };
  } catch {
    return null;
  }
};

// Resolve trackPageVisit eventId for a URL (config keys matched by normalized base + params; longest match wins)
const getTrackPageVisitEventId = (url) => {
  const trackPageVisit = DOMAIN_CONFIG[currentDomain]?.trackPageVisit;
  if (!trackPageVisit || typeof url !== "string") return null;
  const urlNorm = normalizeUrlForTrackPageVisit(url);
  if (!urlNorm) return null;
  const urlFull = urlNorm.base + urlNorm.search;
  let bestKey = null;
  let bestKeyFull = "";
  for (const key of Object.keys(trackPageVisit)) {
    const keyNorm = normalizeUrlForTrackPageVisit(key);
    if (!keyNorm) continue;
    const keyFull = keyNorm.base + keyNorm.search;
    if (
      urlFull.startsWith(keyFull) &&
      (bestKey == null || keyFull.length > bestKeyFull.length)
    ) {
      bestKey = key;
      bestKeyFull = keyFull;
    }
  }
  return bestKey ? (trackPageVisit[bestKey]?.eventId ?? null) : null;
};

const checkTrackPageVisitAndFire = () => {
  const eventId = getTrackPageVisitEventId(window.location.href);
  if (eventId) trackevent({ id: eventId });
};

// We check for page visits on:
// Initial load (once after connect)
// popstate (back/forward)
// hashchange (hash-only navigation)
// pushState / replaceState (wrapped)
const setupTrackPageVisit = () => {
  const trackPageVisit = DOMAIN_CONFIG[currentDomain]?.trackPageVisit;
  if (!trackPageVisit || typeof trackPageVisit !== "object") return;
  checkTrackPageVisitAndFire();
  window.addEventListener("popstate", checkTrackPageVisitAndFire);
  window.addEventListener("hashchange", checkTrackPageVisitAndFire);
  const origPushState = history.pushState;
  const origReplaceState = history.replaceState;
  // wrapping pushState and replaceState to check for page visits
  // Note: If other scripts not under our control also wrap, but without chaining,
  // our wrapper may not be called and page visits may not be tracked
  history.pushState = function (...args) {
    origPushState.apply(this, args);
    checkTrackPageVisitAndFire();
  };
  history.replaceState = function (...args) {
    origReplaceState.apply(this, args);
    checkTrackPageVisitAndFire();
  };
};

window.addEventListener("message", (event) => {
  // Security checks
  // Get allowed origin from DOMAIN_CONFIG dashboardurldefault (considering customerId mapping)
  const dashboardUrl = getDashboardUrlFromDomainConfig();
  if (!dashboardUrl) {
    return;
  }
  const allowedOrigin = new URL(dashboardUrl).origin;

  if (event.origin !== allowedOrigin) {
    console.warn("Blocked message from unauthorized origin:", event.origin);
    return;
  }

  const data = event.data;

  if (data.type === "REQUEST_JWT") {
    // Additional validation
    if (data.targetOrigin && data.targetOrigin !== event.origin) {
      event.source.postMessage(
        {
          type: "JWT_ERROR",
          requestId: data.requestId,
          message: "Origin mismatch",
        },
        event.origin
      );
      return;
    }

    // Send token
    event.source.postMessage(
      {
        type: "JWT_RESPONSE",
        token: jwtToken,
        requestId: data.requestId,
        timestamp: Date.now(),
      },
      event.origin
    );
  }
});

const isSignatureValid = async (data) => {
  const { v24Signature, signature, ...rest } = data;
  const payload = JSON.stringify(rest);

  try {
    const key = await crypto.subtle.importKey(
      "spki",
      Uint8Array.from(
        atob(process.env.BRAIN_MESSAGES_SIGNING_PUBLIC_KEY),
        (c) => c.charCodeAt(0)
      ),
      { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
      false,
      ["verify"]
    );

    return crypto.subtle.verify(
      "RSASSA-PKCS1-v1_5",
      key,
      Uint8Array.from(atob(v24Signature.replace("24-", "")), (c) =>
        c.charCodeAt(0)
      ),
      new TextEncoder().encode(payload)
    );
  } catch (e) {
    logger.error("Signature verification error", e);
    return false;
  }
};

async function _digestMessage(message) {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest("SHA-1", data);

  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  return hashHex.substring(0, 36);
}

/* 
Helper function to sanitize value.
It allows Letters, numbers and some special characters.
Characters allowed: !@#$%^&*()_+-[]{};:'"\|,.<>/?
 */
const sanitizeValue = (value) => {
  if (typeof value === "number") {
    return value;
  }

  if (!value) {
    return null;
  }

  // Remove any HTML tags and attributes
  const strippedInput = value.replace(/(<([^>]+)>)/gi, "");

  // Allow only specific characters
  const allowedChars = /^[a-zA-Z0-9 -àèáéíóúÁÈÉÍÓÚñÑüÜ.,;òùì*£$%&]+$/;
  const sanitizedInput = strippedInput.match(allowedChars)?.join("") ?? null;

  return sanitizedInput;
};

const sanitizeCustomParams = (config) => {
  const sanitizedParams = {};

  // Loop through config keys
  for (const key in config) {
    if (key.startsWith("CUSTOM_")) {
      // Sanitize value of custom params
      const sanitizedValue = sanitizeValue(config[key]);

      // Add sanitized key-value pair to newObj
      sanitizedParams[key] = sanitizedValue;
    }
  }
  return sanitizedParams;
};

const getDevice = async ({ config }) => {
  let did;
  let secret;

  logger.log("getDevice", { config });

  if (config && config.userId && config.userHash) {
    logger.log("Hi loaded userId and userHash from config");
    const userIdHash = await _digestMessage(config.userId);
    const getDidFromHash = (h) => {
      const did =
        h.substring(0, 8) +
        "-" +
        h.substring(8, 12) +
        "-" +
        h.substring(12, 16) +
        "-" +
        h.substring(16, 20) +
        "-" +
        h.substring(20, 32);
      return did;
    };
    return {
      did: getDidFromHash(userIdHash),
      secret: `24-${config.userHash}`,
      provisioningTemplateName: config.customerId || "demo",
      locale: ["it-IT", "en-GB", "fr-FR"].includes(config.language)
        ? config.language
        : null,
    };
  }

  if (config && config.userId && config.jwtToken) {
    logger.log("Hi loaded userId and jwtToken from config");
    jwtToken = config.jwtToken; // Store JWT token for trackevent
    const userIdHash = await _digestMessage(config.userId);
    const getDidFromHash = (h) => {
      const did =
        h.substring(0, 8) +
        "-" +
        h.substring(8, 12) +
        "-" +
        h.substring(12, 16) +
        "-" +
        h.substring(16, 20) +
        "-" +
        h.substring(20, 32);
      return did;
    };
    console.log("Config: ", config);
    return {
      did: getDidFromHash(userIdHash),
      secret: config.jwtToken,
      provisioningTemplateName: config.customerId || "demo",
      locale: ["it-IT", "en-GB", "fr-FR"].includes(config.language)
        ? config.language
        : null,
      isJwt: true,
    };
  }

  if (config && config.userId && !config.userHash && !config.jwtToken) {
    throw new Error(
      "Hi: you must provide both userId and userHash in call to init()"
    );
  }

  // else, normal flow: we generate a new did and secret
  // and save it in localStorage

  try {
    ({ did, secret } = JSON.parse(localStorage.getItem("HI::device")));
  } catch (e) {
  } finally {
    if (!did || !secret) {
      if (!did) {
        did = uuidv4();
      }
      if (!secret) {
        console.log("Generating new secret");
        secret = `24-${uuidv4()}`;
      }

      localStorage.setItem("HI::device", JSON.stringify({ did, secret }));
    }
  }

  return {
    did,
    secret,
    provisioningTemplateName: config.customerId || "demo",
    locale: config.language || null,
  };
};

const getDashboardContainerElement = () => {
  if (appInitData?.dashboardAccess === "inside-app") {
    return document.querySelector("#hi-container");
  }
  return hiQuery("#hi-container");
};

const getDashboardElement = () => {
  if (appInitData?.dashboardAccess === "inside-app") {
    return (
      document.querySelector("#hi-container #hi-dashboard") ??
      document.querySelector("#hi-dashboard")
    );
  }
  return hiQuery("#hi-dashboard");
};

//Helper function to wait for element to be loaded.
//It ll make 3 attemps to get the element (one per second).
//If found: Return element, else throw error.
const waitForElementToLoad = (selector) => {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    let intervalId = setInterval(() => {
      attempts++;
      let element = document.querySelector(selector);
      if (element) {
        clearInterval(intervalId);
        resolve(element);
      } else if (attempts >= 3) {
        clearInterval(intervalId);
        console.error(`Element ${selector} not found`);
        reject(new Error(`Element ${selector} not found`));
      }
    }, 1000);
  });
};

const markDashboardOpened = () => {
  dashboardOpenedAt = Date.now();
};

const isDashboardOutsideCloseGuarded = () =>
  dashboardOpenedAt > 0 &&
  Date.now() - dashboardOpenedAt < DASHBOARD_OUTSIDE_CLOSE_GUARD_MS;

const renderDashboard = ({ did, path = "/", data }) => {
  // Wipe notification session storage
  sessionStorage.removeItem(NOTIFICATION_STORAGE_KEY);
  toggleNotificationIcon(false);
  // Arm the outside-close guard for this open so a same-gesture or delayed
  // ghost click cannot cancel the iframe load (see window click handler).
  markDashboardOpened();
  let dashboard = getDashboardElement();
  let src = "";

  // Check if current domain is in the mapping
  const dashboardUrl = getDashboardUrlFromDomainConfig();
  if (dashboardUrl) {
    // Use mapped domain directly without adding path or did parameter
    src = dashboardUrl;
  } else {
    // Proceed with existing logic
    const regex = /did=/;
    const baseUrl = `https://userdashboard.${process.env.DOMAIN_NAME}`;
    if (regex.test(path)) {
      src = `${baseUrl}${path}`;
    } else {
      try {
        const url = new URL(path, baseUrl);
        url.searchParams.append("did", did);
        src = url.toString();
      } catch (error) {
        // defensive fallback to the separator logic if URL parsing ever fails
        const separator = path.includes("?") ? "&" : "?";
        src = `${baseUrl}${path}${separator}did=${did}`;
      }
    }
  }

  // Helper function to setup dashboard close event listener

  const setupDashboardCloseListener = (iframe, customCloseEvent) => {
    // Only setup listener if customCloseEvent is provided
    if (!customCloseEvent) {
      return;
    }

    // Remove existing listener if any
    if (dashboardMessageHandler) {
      window.removeEventListener("message", dashboardMessageHandler);
      dashboardMessageHandler = null;
    }

    // Pin the trusted origin to whatever URL we just set on the iframe.
    // Mirrors the stimulus postMessage trust model so a third-party page
    // script or unrelated frame cannot spoof the custom close event by
    // posting to window with arbitrary `data.type`. When `src` is not a
    // parseable absolute URL, we fall back to source-only verification
    // (preserves prior behavior for unusual configurations).
    let allowedOrigin = null;
    try {
      allowedOrigin = new URL(src, window.location.href).origin;
    } catch {
      allowedOrigin = null;
    }

    dashboardMessageHandler = (e) => {
      // Check if message is from the dashboard iframe.
      // We re-resolve via getDashboardElement() (instead of using the
      // captured `iframe` arg) so the check stays correct after a
      // renderDashboard re-entry that swaps the dashboard node.
      const dashboardIframe = getDashboardElement()?.querySelector("iframe");
      if (!dashboardIframe || e.source !== dashboardIframe.contentWindow) {
        return;
      }

      // Origin guard: require exact match to the URL we set on the
      // iframe. Skip only when src was unparseable above.
      if (allowedOrigin != null && e.origin !== allowedOrigin) {
        return;
      }

      // Check if the message type matches the custom close event
      if (
        e.data &&
        (e.data.type === customCloseEvent || e.data === customCloseEvent)
      ) {
        removeDashboardContainer();
      }
    };

    window.addEventListener("message", dashboardMessageHandler);
  };

  if (dashboard) {
    const iframe = dashboard.querySelector("iframe");
    iframe.setAttribute("src", src);
    setupDashboardCloseListener(
      iframe,
      DOMAIN_CONFIG[currentDomain]?.customdashboardcloseevent
    );
    return;
  }

  if (data.dashboardAccess === "inside-app") {
    waitForElementToLoad("#hi-container")
      .then((element) => {
        let dashboardContainer = element;
        dashboard = document.createElement("div");
        dashboard.id = "hi-dashboard";
        dashboard.innerHTML = `<iframe frameborder="0" src="${src}" />`;
        dashboardContainer.innerHTML = "";
        dashboardContainer.appendChild(dashboard);
      })
      .catch((error) => {
        console.error(error);
      });
  } else {
    let dashboardContainer = document.createElement("div");
    const blurEffectContainer = document.createElement("div");
    blurEffectContainer.id = "blur-effect";
    dashboardContainer.id = "hi-container";
    dashboardContainer.appendChild(blurEffectContainer);
    dashboard = document.createElement("div");
    dashboard.id = "hi-dashboard";
    dashboard.innerHTML = `<iframe frameborder="0" src="${src}" />`;
    dashboardContainer.appendChild(dashboard);
    ensureHiMount().appendChild(dashboardContainer);
    const iframe = dashboard.querySelector("iframe");
    setupDashboardCloseListener(
      iframe,
      DOMAIN_CONFIG[currentDomain]?.customdashboardcloseevent
    );
  }
};

const renderHI = ({ did, data }) => {
  appInitData = data;
  if (data.dashboardAccess !== "inside-app") {
    if (hiGetElementById("icon-container")) {
      return;
    }
  }

  const barImage = DOMAIN_CONFIG[currentDomain]?.barImage?.[data?.cid];
  const hiWebBtnIcon =
    DOMAIN_CONFIG[currentDomain]?.displayAsBar && barImage
      ? barImage
      : data.hiWebBtnIcon;

  const mobileStyles = `
    @media (max-width: ${mobileTriggerWidth}px) {
      #icon-container {
        width: 100%;
      }
      #hi-dashboard {
        width: 100%;
        height: 90%;
        max-width: unset;
        transform: unset;
        top: unset;
        bottom: 0;
        left: 0;
      }
      #notification-icon {
        width: 24px;
        height: 24px;
        transform: translateY(10%);
      }
    }
    
  `;

  const displayAsBarStyle = `
    width: 70%;
    height: auto;
    bottom: 0;
    right: 50%;
    transform: translateX(50%);
  `;

  const containerZIndex = DOMAIN_CONFIG[currentDomain]?.displayAsBar
    ? 1000
    : Number.MAX_SAFE_INTEGER;
  const styles = document.createElement("style");
  styles.innerHTML = `
    #icon-container {
      position: fixed;
      bottom: 11px;
      right: 11px;
      z-index: ${containerZIndex};
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      ${
        DOMAIN_CONFIG[currentDomain]?.displayAsBar
          ? ""
          : "width: fit-content; max-width: none;"
      }
      ${DOMAIN_CONFIG[currentDomain]?.displayAsBar ? displayAsBarStyle : ""}
    }
    #hi-stimulus {
      position: fixed;
      bottom: 0;
      right: 0;
      z-index: ${Number.MAX_SAFE_INTEGER};
    }
    #hi-stimulus.hi-stimulus--anchored {
      bottom: auto;
      right: auto;
      top: 0;
      left: 0;
    }
    `;

  let root = document.createElement("div");
  root.id = "icon-container";
  if (DOMAIN_CONFIG[currentDomain]?.displayAsBar) {
    root.classList.add("hi-icon-container--bar");
  }
  if (data.dashboardAccess === "inside-app") {
    if (!hiInsideAppObserverStarted) {
      hiInsideAppObserverStarted = true;
      checkForDashboard({ did });
    }
  } else {
    styles.innerHTML =
      styles.innerHTML +
      `#hi-icon {
      cursor: pointer;
      border-radius: 12px;
      background: #241751;
      width: 38px;
      height: 38px;
      box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.12);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    @keyframes hiw-fadeInFromBottom {
      from {
        opacity: 0;
        transform: translate(-50%, -40%);
      }
      to {
        opacity: 1;
        transform: translate(-50%, -50%);
      }
    }

    @keyframes hiw-fadeOut {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }

    @keyframes hiw-fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    #hi-container {
      width: 100%;
      height: 100%;
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 10000;
      ${
        DOMAIN_CONFIG[currentDomain]?.displayAsBar
          ? `animation: hiw-fadeInFromBottom 0.5s ease-out;`
          : ""
      }
    }

    #blur-effect {
      backdrop-filter: blur(10px);
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: -1;
    }

    #hi-dashboard {
      z-index: ${Number.MAX_SAFE_INTEGER};
      width: 90%;
      max-width: ${
        DOMAIN_CONFIG[currentDomain]?.displayAsBar ? "unset" : "1242.5px"
      };
      height: 90%;
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      border-radius: 18px;
      border: 2px solid  #241751;
      background-color: rgb(249, 249, 249);
      box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.12);
      overflow: hidden;

      ${
        DOMAIN_CONFIG[currentDomain]?.displayAsBar
          ? `
        top: unset;
        bottom: 0;
        transform: translateX(-50%);
      `
          : ""
      }
    }
    
    #hi-dashboard iframe {
      width: 100%;
      height: 100%;
    }
    
    .hi-web-custom-btn-icon {
      cursor: pointer;
      display: block;
      height: 100%;
      width: auto;
      max-width: 100%;
      object-fit: contain;
    }
    #icon-container.hi-icon-container--bar .hi-web-custom-btn-icon {
      width: 100%;
      height: 100%;
      max-width: none;
    }
    #notification-icon {
      position: absolute;
      top: 0;
      right: 3.5%;
      width: clamp(16px, 1.67vw, 32px);
      height: clamp(16px, 1.67vw, 32px);
      background-color: #FF0000;
      border-radius: 50%;
      filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.7));
      display: none;
      cursor: pointer;
    } 
    ${
      DOMAIN_CONFIG[currentDomain]?.displayAsBar &&
      DOMAIN_CONFIG[currentDomain]?.displayOnMobile
        ? mobileStyles
        : ""
    }
    `;

    if (hiWebBtnIcon) {
      root.innerHTML = `<img class="hi-web-custom-btn-icon" id="hi-web-custom-icon" src="${hiWebBtnIcon}" alt="">`;
      syncCustomIconIntrinsicSize(root.querySelector("#hi-web-custom-icon"));
    } else {
      root.innerHTML = `
     <div id="hi-icon">
      <svg width="18" height="18" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M28.093 7.233c1.012 0 1.863-.347 2.551-1.042.718-.71 1.076-1.553 1.076-2.532a3.487 3.487 0 0 0-1.076-2.574C29.927.362 29.077 0 28.093 0c-.97 0-1.807.362-2.51 1.085-.69.695-1.034 1.553-1.034 2.574 0 .993.345 1.837 1.034 2.532.689.695 1.525 1.042 2.51 1.042ZM5.694 31.72V20.615c0-1.73.507-3.113 1.519-4.149 1.04-1.05 2.264-1.574 3.67-1.574 1.279 0 2.274.355 2.984 1.064.71.709 1.065 1.751 1.065 3.127V31.72h5.715V17.04c0-2.453-.703-4.304-2.109-5.552-1.42-1.262-3.31-1.893-5.673-1.893a9.714 9.714 0 0 0-4.07.893c-1.308.596-2.341 1.383-3.1 2.362V.34H0v31.38h5.694Zm25.288-21.53v21.53h-5.695V10.19h5.695Z" fill="#94f1b2"/></svg>
     </div>`;
    }

    root.addEventListener("click", (e) => {
      // Keep this click off the window outside-close listener. Otherwise the
      // same bubbling gesture that opens the dashboard can also tear it down
      // (iframe request shows as canceled). composedPath() usually prevents
      // that, but stopPropagation makes open independent of path checks.
      e.stopPropagation();
      const dashboardcontainer = getDashboardContainerElement();
      if (!dashboardcontainer) {
        renderDashboard({ did, data: appInitData });
        if (DOMAIN_CONFIG[currentDomain]?.displayAsBar) {
          openIconContainerAnimation();
        }
      } else {
        removeDashboardContainer();
      }
    });

    window.addEventListener("click", (e) => {
      // Ignore outside-closes shortly after open. Covers delayed "ghost"
      // clicks (touch/pen/trackpad) that land on the blur layer — which is
      // intentionally "outside" #hi-dashboard — and would cancel the iframe.
      if (isDashboardOutsideCloseGuarded()) {
        return;
      }
      const dashboard = getDashboardElement();
      // Use composedPath() so this works for shadow-DOM-hosted nodes
      // (e.target is retargeted to #hi-web-root for clicks inside the
      // shadow tree, so plain .contains() against #icon-container or
      // #hi-dashboard would always return false and immediately tear
      // the dashboard down on the same click that just opened it).
      const path =
        typeof e.composedPath === "function" ? e.composedPath() : null;
      const isInside = (node) => {
        if (!node) return false;
        // Empty path is truthy but means "unknown"; fall back to contains().
        if (path && path.length > 0) {
          return path.includes(node);
        }
        return node.contains(e.target);
      };
      if (isInside(root) || isInside(dashboard)) {
        return;
      }
      removeDashboardContainer();
    });
    const shadow = ensureHiMount();
    shadow.appendChild(styles);
    shadow.appendChild(root);

    let notiticationElement = document.createElement("div");
    notiticationElement.id = "notification-icon";
    root.appendChild(notiticationElement);

    if (
      DOMAIN_CONFIG[currentDomain]?.displayAsBar &&
      DOMAIN_CONFIG[currentDomain]?.displayOnMobile
    ) {
      window.addEventListener("resize", () => checkForMobileDisplay(data?.cid));
    }

    if (
      DOMAIN_CONFIG[currentDomain]?.openFirstTime &&
      isFirstTimeOpening(data?.userstateStartedOn)
    ) {
      renderDashboard({ did, data: appInitData });
      openIconContainerAnimation();
      localStorage.setItem("hiweb-opened", "true");
    }
  }
  if (DOMAIN_CONFIG[currentDomain]?.displayOnMobile) {
    checkForMobileDisplay(data?.cid); // Check mobile display on load
  }

  // Check notification and start the notification interval
  if (DOMAIN_CONFIG[currentDomain]?.notificationEnabled) {
    checkNotification().then(() => {
      scheduleNotificationInterval();
    });
  }
};

const scheduleNotificationInterval = () => {
  // Schedule notification check every 5 minutes
  // If sessionStorage already has a lastCheck, wait only the remaining
  // time until 5 minutes after that timestamp, then reset to 5 min.
  let initialDelay = NOTIFICATION_CHECK_MS;
  try {
    const raw = sessionStorage.getItem(NOTIFICATION_STORAGE_KEY);
    if (raw) {
      const state = JSON.parse(raw);
      if (state.lastCheck) {
        const elapsed = Date.now() - state.lastCheck;
        if (elapsed < NOTIFICATION_CHECK_MS) {
          initialDelay = NOTIFICATION_CHECK_MS - elapsed;
        } else {
          initialDelay = 0;
        }
      }
    }
  } catch (_) {
    // invalid JSON, fall back to default interval
  }

  if (initialDelay !== NOTIFICATION_CHECK_MS && notificationInterval === null) {
    // First tick uses the adjusted delay, then switch to regular interval
    notificationInterval = setTimeout(() => {
      checkNotification();
      notificationInterval = setInterval(
        checkNotification,
        NOTIFICATION_CHECK_MS
      );
    }, initialDelay);
  } else {
    notificationInterval = setInterval(
      checkNotification,
      NOTIFICATION_CHECK_MS
    );
  }
};

const isFirstTimeOpening = (userStateStartedOn) => {
  const openedCheck = localStorage.getItem("hiweb-opened");
  if (userStateStartedOn) {
    const oneMinuteInMs = 60 * 1000; // 1 minute in milliseconds
    const timeElapsed = Date.now() - userStateStartedOn;
    if (timeElapsed < oneMinuteInMs && !openedCheck) {
      return true;
    } else {
      return false;
    }
  }

  if (!openedCheck) {
    return true;
  } else {
    return false;
  }
};

const toggleNotificationIcon = (show = true) => {
  const notificationIcon = hiGetElementById("notification-icon");
  if (notificationIcon) {
    notificationIcon.style.display = show ? "block" : "none";
  } else {
    console.log("[toggleNotificationIcon] notification icon element not found");
  }
};

const checkForMobileDisplay = (cid) => {
  if (!cid) {
    return;
  }
  if (
    DOMAIN_CONFIG[currentDomain]?.displayAsBar &&
    window.innerWidth <= mobileTriggerWidth
  ) {
    const hiWebCustomIcon = hiGetElementById("hi-web-custom-icon");
    if (hiWebCustomIcon) {
      hiWebCustomIcon.src = DOMAIN_CONFIG[currentDomain]?.barMobileImage;
      syncCustomIconIntrinsicSize(hiWebCustomIcon);
    }
  } else if (DOMAIN_CONFIG[currentDomain]?.displayAsBar) {
    const hiWebCustomIcon = hiGetElementById("hi-web-custom-icon");
    if (hiWebCustomIcon) {
      hiWebCustomIcon.src = DOMAIN_CONFIG[currentDomain]?.barImage?.[cid];
      syncCustomIconIntrinsicSize(hiWebCustomIcon);
    }
  }
};

// Animations for the bar and iframe if displayAsBar is true
const closeIconContainerAnimation = () => {
  const root = hiGetElementById("icon-container");
  if (!root || root.style.visibility === "visible") {
    return;
  }
  root.style.visibility = "visible";
  root.style.opacity = "0";
  root.style.animation = "hiw-fadeIn 0.3s ease-out";
  root.addEventListener(
    "animationend",
    () => {
      root.style.opacity = "1";
      root.style.animation = "";
    },
    { once: true }
  );
};
const openIconContainerAnimation = () => {
  const root = hiGetElementById("icon-container");
  if (!root) {
    return;
  }
  root.style.animation = "hiw-fadeOut 0.3s ease-out";
  root.addEventListener(
    "animationend",
    () => {
      root.style.visibility = "hidden";
      root.style.animation = "";
    },
    { once: true }
  );
};

const removeDashboardContainer = () => {
  const dashboardcontainer = getDashboardContainerElement();
  if (dashboardcontainer) {
    dashboardcontainer.remove();
  }
  // Clean up the dashboard message listener if it exists
  if (dashboardMessageHandler) {
    window.removeEventListener("message", dashboardMessageHandler);
    dashboardMessageHandler = null;
  }

  if (DOMAIN_CONFIG[currentDomain]?.displayAsBar) {
    closeIconContainerAnimation();
  }
};

const getStimulusTrustRejection = (e) => {
  if (!stimulusTrustedWindow) {
    return "no_trusted_window";
  }
  if (e.source !== stimulusTrustedWindow) {
    return "source_mismatch";
  }
  if (stimulusTrustedOrigin != null && e.origin !== stimulusTrustedOrigin) {
    return "origin_mismatch";
  }
  return null;
};

const logRejectedStimulusRendered = (e, { width, height, reason }) => {
  logger.info("Stimulus rendered message rejected", {
    reason,
    eventOrigin: e.origin,
    expectedOrigin: stimulusTrustedOrigin,
    width,
    height,
  });
};

const repinStimulusTrustOnIframeLoad = (iframe, templateUrl) => {
  if (!iframe) {
    return;
  }
  let templateOrigin = null;
  try {
    templateOrigin = new URL(templateUrl, window.location.href).origin;
  } catch {
    templateOrigin = null;
  }

  iframe.addEventListener(
    "load",
    () => {
      if (!iframe.isConnected) {
        return;
      }
      stimulusTrustedWindow = iframe.contentWindow ?? null;
      try {
        const loadedOrigin = new URL(iframe.contentWindow.location.href).origin;
        const previousOrigin = stimulusTrustedOrigin;
        stimulusTrustedOrigin = loadedOrigin;
        if (
          loadedOrigin !== templateOrigin ||
          loadedOrigin !== previousOrigin
        ) {
          logger.info("Stimulus trusted origin re-pinned after iframe load", {
            templateOrigin,
            previousOrigin,
            loadedOrigin,
            templateUrl,
          });
        }
      } catch {
        stimulusTrustedOrigin = null;
        logger.info(
          "Stimulus iframe loaded cross-origin; postMessage trust is source-only",
          { templateOrigin, templateUrl }
        );
      }
    },
    { once: true }
  );
};

const handleMessageEvent = (e, did) => {
  windowMessageEvtListener = true;
  try {
    const { type, width, height, url } = e.data ?? {};

    if (type === "rendered" || type === "closed" || type === "openURL") {
      const trustRejection = getStimulusTrustRejection(e);
      if (trustRejection) {
        if (type === "rendered") {
          logRejectedStimulusRendered(e, {
            width,
            height,
            reason: trustRejection,
          });
        }
        return;
      }
    }

    if (type === "rendered") {
      const hiStimulus = hiQuery("#hi-stimulus");
      if (!hiStimulus) {
        logRejectedStimulusRendered(e, {
          width,
          height,
          reason: "stimulus_element_missing",
        });
        return;
      }
      hiStimulus.style.width = width + "px";
      hiStimulus.style.height = height + "px";
      const iframe = hiStimulus.querySelector("iframe");
      if (iframe) {
        iframe.setAttribute("width", width);
        iframe.setAttribute("height", height);
      }
      if (
        stimulusAnchorTarget &&
        hiStimulus.classList.contains("hi-stimulus--anchored")
      ) {
        requestAnimationFrame(() =>
          updateStimulusAnchorPosition(hiStimulus, stimulusAnchorTarget)
        );
      }
      return;
    }

    if (type === "closed") {
      const iframe = hiQuery("#hi-stimulus iframe");
      iframe?.parentNode?.remove();
      clearStimulusPositionListeners();
      stimulusAnchorTarget = null;
      stimulusTrustedWindow = null;
      stimulusTrustedOrigin = null;
      removeNotification();
      // Only show the icon-container if dashboard is not open (when displayAsBar is true)
      // Otherwise the bar would reappear when closing the stimulus after opening the dashboard
      if (!getDashboardContainerElement()) {
        const iconContainer = hiGetElementById("icon-container");
        iconContainer ? (iconContainer.style.visibility = "visible") : null;
      }
      return;
    }

    if (type === "openURL") {
      const regexp = new RegExp(
        `^https?:\/\/userdashboard\\.${process.env.DOMAIN_NAME.replace(
          ".",
          "\\."
        )}`
      );
      if (url.startsWith("hicoach://dashboard") || url.match(regexp)) {
        const path = url.replace("hicoach://dashboard", "").replace(regexp, "");

        if (!getDashboardContainerElement()) {
          // Get the element by its ID
          const myElement = document.getElementById(
            window.HiConfig.customButonId
          );

          if (myElement) {
            // Click the element
            myElement.click();
            return;
          } else {
            renderDashboard({ did, path, data: appInitData });
            if (DOMAIN_CONFIG[currentDomain]?.displayAsBar) {
              openIconContainerAnimation();
            }
            return;
          }
        }
        renderDashboard({ did, path, data: appInitData });
        if (DOMAIN_CONFIG[currentDomain]?.displayAsBar) {
          openIconContainerAnimation();
        }
        return;
      }

      window.open(url, "_blank");
    }
    // eslint-disable-next-line no-empty
  } catch (e) {}
};
const renderStimulus = ({ did, templateUrl, size, target }) => {
  // On mobile device do not render the tip
  if (screen.width <= 884 && !DOMAIN_CONFIG[currentDomain]?.displayOnMobile) {
    return;
  }

  clearStimulusPositionListeners();
  stimulusTrustedWindow = null;
  stimulusTrustedOrigin = null;
  stimulusAnchorTarget = null;

  let stimulus = hiQuery("#hi-stimulus");
  showNotification();
  if (stimulus) {
    stimulus.remove();
  }

  stimulus = document.createElement("div");
  stimulus.id = "hi-stimulus";
  stimulus.innerHTML = `<iframe frameborder="0" src="${templateUrl}" width="${size.width}" height="${size.height}" />`;
  stimulus.addEventListener("click", function (event) {
    // Prevent the event from bubbling up to the parent elements
    event.stopPropagation();
  });

  const shadow = ensureHiMount();
  if (target && target.isConnected) {
    stimulus.classList.add("hi-stimulus--anchored");
    stimulusAnchorTarget = target;
    const onViewportChange = debounce(
      () => updateStimulusAnchorPosition(stimulus, target),
      32
    );
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    stimulusPositionCleanup = () => {
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
      if (typeof onViewportChange.cancel === "function") {
        onViewportChange.cancel();
      }
    };
    requestAnimationFrame(() => updateStimulusAnchorPosition(stimulus, target));
  } else {
    stimulus.classList.remove("hi-stimulus--anchored");
  }

  if (!DOMAIN_CONFIG[currentDomain]?.displayAsBar) {
    const iconContainer = hiGetElementById("icon-container");
    iconContainer ? (iconContainer.style.visibility = "hidden") : null;
  }
  shadow.appendChild(stimulus);

  const iframeEl = stimulus.querySelector("iframe");
  stimulusTrustedWindow = iframeEl?.contentWindow ?? null;
  // Defer origin pin until load — redirects may change origin; cross-origin
  // documents cannot be read here and rely on source-only trust until then.
  stimulusTrustedOrigin = null;
  repinStimulusTrustOnIframeLoad(iframeEl, templateUrl);

  !windowMessageEvtListener &&
    window.addEventListener("message", (e) => handleMessageEvent(e, did));
};

const initTriggers = ({ did }) => {
  const triggers = [
    {
      selector: 'input[hi-web-id="semantics-search"]',
      event: "input",
      selectorName: "ContentDashboardSemanticsSearch",
    },
    {
      selector: 'button[hi-web-id="semantics-export-button"]',
      event: "click",
      selectorName: "ContentDashboardSemanticsExport",
    },
    {
      selector: 'button[data-hi_id="subscribeButton"]',
      event: "click",
      selectorName: "SapSubscribe",
    },
    {
      selector: 'bdi[id="__label0-bdi"]',
      event: "mouseenter",
      selectorName: "SapNewEventsLabel",
    },
    {
      selector: 'span[id="__box0-arrow"]',
      event: "click",
      selectorName: "SapDropdownRelationToSap",
    },
    {
      selector: 'p[title="Contact Owner"]',
      event: "presence",
      selectorName: "SalesforceContactPage",
    },
    {
      selector: "span.global-nav__primary-link-text",
      event: "presence",
      selectorName: "LinkedinAnyPage",
    },
    {
      selector: 'img[id="hi-risorse"]',
      event: "presence",
      selectorName: "WebsiteRisorseTab",
    },
    {
      selector: 'img[id="hi-piattaforma"]',
      event: "presence",
      selectorName: "WebsitePiattaformaTab",
    },
    {
      selector: 'img[id="hi-home"]',
      event: "presence",
      selectorName: "WebsiteHomeTab",
    },
    {
      selector: 'h1[hi-trigger="marketplace"]',
      event: "presence",
      selectorName: "AdvMarketplace",
    },
    {
      selector: 'h1[hi-trigger="news"]',
      event: "presence",
      selectorName: "AdvNews",
    },
    {
      selector: 'h1[hi-trigger="magazine"]',
      event: "presence",
      selectorName: "AdvMagazine",
    },
    {
      selector: 'h1[hi-trigger="insight"]',
      event: "presence",
      selectorName: "AdvInsight",
    },
    {
      selector: 'h1[hi-trigger="events"]',
      event: "presence",
      selectorName: "AdvEvents",
    },
    {
      selector: 'h1[hi-trigger="assessment"]',
      event: "presence",
      selectorName: "AdvAssessment",
    },
    {
      selector: 'h1[hi-trigger="NuovaCampagna"]',
      event: "presence",
      selectorName: "AdvNuovaCampagna",
    },
    {
      selector: 'h1[hi-trigger="template"]',
      event: "presence",
      selectorName: "AdvTemplate",
    },
    {
      selector: 'h1[hi-trigger="deliverable"]',
      event: "presence",
      selectorName: "AdvDeliverable",
    },
    {
      selector: 'h1[hi-trigger="education"]',
      event: "presence",
      selectorName: "AdvEducation",
    },
    {
      selector: 'h1[hi-trigger="users"]',
      event: "presence",
      selectorName: "AdvUtenti",
    },
    {
      selector: 'h1[hi-trigger="aziende"]',
      event: "presence",
      selectorName: "AdvAziende",
    },
    {
      selector: 'h1[hi-trigger="cluster"]',
      event: "presence",
      selectorName: "AdvCluster",
    },
    {
      selector: 'h1[hi-trigger="newsletter"]',
      event: "presence",
      selectorName: "AdvNewsletter",
    },
    {
      selector: 'h1[hi-trigger="dashboard"]',
      event: "presence",
      selectorName: "AdvDashboard",
    },
    {
      selector: 'h1[hi-trigger="search"]',
      event: "presence",
      selectorName: "AdvRicercaTrasversale",
    },
    {
      selector: 'h1[hi-trigger="PreferenzeUtente"]',
      event: "presence",
      selectorName: "AdvPreferenzeUtente",
    },
    {
      selector: 'button[data-automation-id="sp-comment-post"]',
      event: "click",
      selectorName: "SpPostComment",
    },
  ].map((trigger) => ({
    ...trigger,
    listener: debounce((e) => {
      selectorNameElementMap[trigger.selectorName] = e.target;
      sendMessage(did, {
        type: "ElementFound",
        selectorName: trigger.selectorName,
      });
    }, 500),
  }));

  // list of selectornames that already triggered
  // the presence selector:
  const presenceTriggered = [];

  const observer = new MutationObserver(
    debounce(() => {
      triggers.forEach(({ selector, event, selectorName, listener }) => {
        const element = document.querySelector(selector);

        if (element && event === "presence") {
          if (presenceTriggered.includes(selectorName)) {
            // selector already matched
            return;
          }

          selectorNameElementMap[selectorName] = element;
          sendMessage(did, {
            type: "ElementFound",
            selectorName,
          });

          presenceTriggered.push(selectorName);
          return;
        }

        element?.removeEventListener(event, listener);
        element?.addEventListener(event, listener, true);
      });
    })
  );

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

const handleMessage = async (data, did) => {
  logger.info("Brain response received", { data, did });
  if (data.type === "app-init-r") {
    cacheAppInitResponse(did, data);
    handleAppInitResponse(data, did);

    return;
  }

  if (data.type === "dp-r" && data.t != null && did) {
    try {
      localStorage.setItem(
        `${FIRST_ACTIVITY_RECEIVED_STORAGE_KEY_PREFIX}${did}`,
        String(data.t)
      );
    } catch (e) {
      logger.error("Failed to store FirstActivity received time", e);
    }
  }

  if (data.type !== "dp-r" || !data.payload2) {
    return;
  }

  const { templateUrl, size, selectorName } = data.payload2;

  if (!templateUrl) {
    return;
  }

  renderStimulus({
    did,
    templateUrl,
    size,
    target: selectorNameElementMap[selectorName],
  });

  if (templateUrl.includes("scenario=fromLearningMaterial")) {
    removeDashboardContainer();
  }
};

const sendMessage = (did, payload) => {
  const message = {
    did,
    eid: uuidv4(),
    t: Date.now(),
    type: "dp",
    v: 3,
    p: payload,
  };

  if (socket.readyState !== 1) {
    return;
  }

  logger.info("Send message => ", message);
  socket.send(JSON.stringify(message));
};

const getFirstActivityReceivedAt = (did) => {
  try {
    const v = localStorage.getItem(
      `${FIRST_ACTIVITY_RECEIVED_STORAGE_KEY_PREFIX}${did}`
    );
    return v ? Number(v) : 0;
  } catch (e) {
    return 0;
  }
};

const shouldSendFirstActivity = (did) => {
  const debounceMs =
    DOMAIN_CONFIG[currentDomain]?.debounceFirstActivityInterval;
  if (!debounceMs) return true;
  const receivedAt = getFirstActivityReceivedAt(did);
  if (!receivedAt) return true;
  return Date.now() - receivedAt > debounceMs;
};

const ensureFirstActivityInterval = (did) => {
  if (firstActivityInterval) {
    return;
  }

  if (shouldSendFirstActivity(did)) {
    sendMessage(did, {
      type: "FirstActivity",
      channel: "embedded",
    });
  }

  firstActivityInterval = setInterval(() => {
    if (shouldSendFirstActivity(did)) {
      sendMessage(did, {
        type: "FirstActivity",
        channel: "embedded",
      });
    }
  }, FIRST_ACTIVITY_INTERVAL_MS);
};

function handleAppInitResponse(data, did) {
  if (data.channel !== "embedded") {
    console.log(
      "AppInit response received for non-embedded channel, skipping rendering"
    );
    return;
  }
  initTriggers({ did });
  renderHI({ did, data });

  if (data.dashboardAccess === "inside-app") {
    checkForDashboard({ did });
    if (getDashboardContainerElement()) {
      renderDashboard({ did, data: appInitData });
    }
  }

  ensureFirstActivityInterval(did);
}

const connect = async ({ config, appInit = false }) => {
  const { did, provisioningTemplateName, secret, locale, isJwt } =
    await getDevice({
      config,
    });

  const token = btoa(`${did}:${secret}`);
  socket = isJwt
    ? new WebSocket(
        `wss://wss.${process.env.DOMAIN_NAME}?authorization=${token}&cid=${provisioningTemplateName}&jwt=true`
      )
    : new WebSocket(
        `wss://wss.${process.env.DOMAIN_NAME}?authorization=${token}&cid=${provisioningTemplateName}`
      );

  socket.addEventListener("open", () => {
    if (appInit) {
      const shouldSend = shouldSendAppInit(did);
      if (shouldSend) {
        logger.info("Send AppInit event");
        const customParams = sanitizeCustomParams(config);
        sendMessage(did, {
          type: "AppInit",
          channel: "embedded",
          version: `hi-web-${process.env.VERSION}`,
          provisioningTemplateName,
          locale,
          ...customParams,
        });
        logger.info("AppInit sent; response will be cached on receipt", {
          did,
        });
      } else {
        logger.info("Skipping AppInit (cached)", { did });
        const cachedAppInitResponse = getCachedAppInitResponse(did);
        if (cachedAppInitResponse) {
          handleAppInitResponse(cachedAppInitResponse, did);
        }
      }
    }
  });

  socket.addEventListener("close", (e) => {
    logger.info("Socket closed =>", e.reason);
    setTimeout(() => {
      connect({ config });
    }, 1000);
  });

  socket.addEventListener("error", (e) => {
    logger.error("Socket error => ", e.message);
    socket.close();
  });

  socket.addEventListener("message", async (event) => {
    logger.groupCollapsed("Socket received message");
    logger.info("Raw => ", event.data);
    const data = JSON.parse(event.data);
    logger.info("Parsed => ", data);
    logger.groupEnd();

    if (!(await isSignatureValid(data))) {
      logger.error("Message signature is not valid");
      return;
    }

    await handleMessage(data, did);
  });
};

//Helper function to listen for changes in the DOM
//If the "hi-container" element is found, then render the dashboard
const checkForDashboard = ({ did }) => {
  // Select the node that will be observed for mutations
  const targetNode = document.body;

  // Options for the observer (which mutations to observe)
  const config = { childList: true, subtree: true };

  // Callback function to execute when mutations are observed
  const callback = function (mutationsList, observer) {
    for (const mutation of mutationsList) {
      if (mutation.type === "childList") {
        for (const addedNode of mutation.addedNodes) {
          if (
            addedNode.nodeType === Node.ELEMENT_NODE &&
            addedNode.id === "hi-container"
          ) {
            renderDashboard({ did, data: appInitData });
          }
        }
      }
    }
  };

  const observer = new MutationObserver(callback);
  observer.observe(targetNode, config);
};

const handleVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    socket.close();
  }
};

// Function to show the notification
const showNotification = () => {
  if (window.HiConfig.showNotification) {
    let favicon = document.getElementById(window.HiConfig.iconId);
    if (window.HiConfig.showBadge && favicon) {
      favIconUrl = favicon.href;
      let faviconSize = 16;
      let canvas = document.createElement("canvas");
      canvas.width = faviconSize;
      canvas.height = faviconSize;

      let context = canvas.getContext("2d");
      let img = document.createElement("img");
      img.src = favicon.href;

      img.onload = () => {
        // Draw Original Favicon as Background
        context.drawImage(img, 0, 0, faviconSize, faviconSize);

        // Draw Notification Circle
        context.beginPath();
        context.arc(
          canvas.width - faviconSize / 3,
          faviconSize / 3,
          faviconSize / 5, //change this number to change the size of the circle
          0,
          2 * Math.PI
        );
        context.fillStyle = "#FF0000";
        context.fill();

        // Replace favicon
        favicon.href = canvas.toDataURL("image/png");
      };
    } else {
      let title = document.querySelector("title");
      title.classList.add("notification-title");
      title.innerText = "(1) " + title.innerText;
      document.title = title.innerText;
    }
  }
};

// Function to remove the notification
const removeNotification = () => {
  if (window.HiConfig.showNotification) {
    let favicon = document.getElementById(window.HiConfig.iconId);
    if (window.HiConfig.showBadge && favicon) {
      favicon.href = favIconUrl;
    } else {
      let title = document.querySelector("title");
      title.classList.remove("notification-title");
      title.innerText = title.innerText.replace("(1) ", "");
      document.title = title.innerText;
    }
  }
};

const init = () => {
  // Check if screen width is greater than 884px
  if (screen.width > 884 || DOMAIN_CONFIG[currentDomain]?.displayOnMobile) {
    // Load the script only if screen width is greater than 884px
    const config = window.HiConfig || {};

    // Check if currentDomain has useridswhitelist and if userId is whitelisted
    const domainConfig = DOMAIN_CONFIG[currentDomain];
    if (domainConfig?.isDisabled === true) {
      logger.log("Hi: Domain is disabled, skipping connection");
      return;
    }
    if (
      domainConfig?.useridswhitelist &&
      Array.isArray(domainConfig.useridswhitelist) &&
      domainConfig.useridswhitelist.length > 0
    ) {
      // If whitelist exists, only proceed if userId is in the whitelist
      if (
        !config.userId ||
        !domainConfig.useridswhitelist.includes(config.userId)
      ) {
        logger.log("Hi: User ID not in whitelist, skipping connection");
        return;
      }
    }

    connect({ config, appInit: true })
      .then(() => {
        logger.log("Hi: Connected");
        document.addEventListener("visibilitychange", handleVisibilityChange);
        setupTrackPageVisit();
      })
      .catch((e) => {
        logger.error("Hi: Error", e);
      });
  }
};

const getEndpointForDomain = (domain) => {
  // Get the dashboard URL using the same logic as getDashboardUrlFromDomainConfig
  const dashboardUrl = getDashboardUrlFromDomainConfig();

  if (!dashboardUrl) {
    logger.error(`No dashboard URL found for domain: ${domain}`);
    return null;
  }

  try {
    // Parse the dashboard URL
    const url = new URL(dashboardUrl);

    // Remove "www." prefix if present, then prefix with "be."
    let hostname = url.hostname;
    if (hostname.startsWith("www.")) {
      hostname = hostname.substring(4); // Remove "www."
    }
    const beHostname = `be.${hostname}`;

    return `${url.protocol}//${beHostname}`;
  } catch (error) {
    logger.error(`Error constructing endpoint for domain: ${domain}`, error);
    return null;
  }
};

/*const getAnalyticEndpointForDomain = (domain) => {
  // Get the dashboard URL using the same logic as getDashboardUrlFromDomainConfig
  const dashboardUrl = getDashboardUrlFromDomainConfig();

  if (!dashboardUrl) {
    logger.error(`No dashboard URL found for domain: ${domain}`);
    return null;
  }

  try {
    // Parse the dashboard URL
    const url = new URL(dashboardUrl);

    // Remove "www." prefix if present, then prefix with "be."
    let hostname = url.hostname;
    if (hostname.startsWith("www.")) {
      hostname = hostname.substring(4); // Remove "www."
    }
    const beHostname = `be.${hostname}`;

    // Construct the analytic endpoint with the new hostname and path
    const analyticEndpoint = `${url.protocol}//${beHostname}/events/track/external`;

    return analyticEndpoint;
  } catch (error) {
    logger.error(
      `Error constructing analytic endpoint for domain: ${domain}`,
      error
    );
    return null;
  }
};*/

const trackevent = async ({ id }) => {
  try {
    if (!id) {
      return { success: false, error: "Event id is required" };
    }

    if (!jwtToken) {
      return {
        success: false,
        error:
          "JWT token not available. Please ensure Hi.init() was called with jwtToken.",
      };
    }

    // Simulate trackevent if SIMULATETRACKEVENT is true
    if (SIMULATETRACKEVENT) {
      logger.info("Received trackevent with id:", id);
      await new Promise((resolve) => setTimeout(resolve, 500));
      logger.info("Event transmitted");
      return { success: true };
    }

    // Get endpoint for current domain
    const beEndpoint = getEndpointForDomain(currentDomain);
    if (!beEndpoint) {
      return {
        success: false,
        error: `No backend endpoint configured for domain: ${currentDomain}`,
      };
    }
    const endpoint = `${beEndpoint}/events/track/external`;

    // Prepare the request payload
    const payload = {
      id,
      timestamp: Date.now(),
    };

    // Use fetch with keepalive: true for reliability (similar to sendBeacon but allows response)
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-External-Auth": jwtToken,
      },
      body: JSON.stringify(payload),
      keepalive: true, // Ensures request completes even if page is closing
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error("Trackevent error:", errorText);
      return { success: false, error: `HTTP ${response.status}: ${errorText}` };
    }

    const result = await response.json();

    // Return success if the backend confirms success
    if (result.success === true) {
      // Check notification and start the notification interval
      if (DOMAIN_CONFIG[currentDomain]?.notificationEnabled) {
        // Wipe notification session storage
        sessionStorage.removeItem("notificationState");
        toggleNotificationIcon(false);
        // Check notification and start the notification interval
        checkNotification().then(() => {
          scheduleNotificationInterval();
        });
      }
      return { success: true };
    } else {
      return {
        success: false,
        error: result.error || "Unknown error from backend",
      };
    }
  } catch (error) {
    logger.error("Trackevent exception:", error);
    return { success: false, error: error.message || "Failed to track event" };
  }
};

const fetchNotifications = async () => {
  try {
    if (!jwtToken) {
      return {
        success: false,
        error:
          "JWT token not available. Please ensure Hi.init() was called with jwtToken.",
      };
    }

    const beEndpoint = getEndpointForDomain(currentDomain);
    if (!beEndpoint) {
      return {
        success: false,
        error: `No backend endpoint configured for domain: ${currentDomain}`,
      };
    }
    const endpoint = `${beEndpoint}/notify`;

    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-External-Auth": jwtToken,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMsg =
        errorData?.message || errorData?.error || `HTTP ${response.status}`;
      logger.error("checkNotification error:", errorMsg);
      return {
        success: false,
        error: errorMsg,
      };
    }

    const result = await response.json();

    if (result.success === true) {
      return {
        success: true,
        notify_count: result.notify_count,
        notifications: result.notifications,
      };
    } else {
      return {
        success: false,
        error: result.error || "Unknown error from backend",
      };
    }
  } catch (error) {
    logger.error("checkNotification exception:", error);
    return {
      success: false,
      error: error.message || "Failed to check notifications",
    };
  }
};

const checkNotification = async () => {
  console.log("[checkNotification] called");
  const raw = sessionStorage.getItem(NOTIFICATION_STORAGE_KEY);
  if (raw) {
    try {
      const state = JSON.parse(raw);
      if (
        state.lastCheck &&
        Date.now() - state.lastCheck < NOTIFICATION_CHECK_MS
      ) {
        if (state.haveNotification === true) {
          toggleNotificationIcon(true);
        }
        return;
      }
    } catch (_) {
      // invalid JSON, continue to fetch
    }
  }
  console.log("[checkNotification] fetching notifications");
  const notificationsResult = await fetchNotifications();
  const lastCheck = Date.now();
  let haveNotification = false;

  if (notificationsResult.success === true) {
    haveNotification = (notificationsResult.notify_count || 0) > 0;
    sessionStorage.setItem(
      NOTIFICATION_STORAGE_KEY,
      JSON.stringify({ lastCheck, haveNotification })
    );
    toggleNotificationIcon(haveNotification);
    console.log("[checkNotification] notifications fetched", haveNotification);
  } else {
    logger.error("checkNotification error:", notificationsResult.error);
    sessionStorage.setItem(
      NOTIFICATION_STORAGE_KEY,
      JSON.stringify({ lastCheck, haveNotification })
    );
    console.log("[checkNotification] notifications fetched", haveNotification);
  }
};

window.Hi = {
  init,
  trackevent,
  checkNotification,
};
